import React, { useCallback } from "react";
import { Linkedin, Facebook, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { track } from "@/hooks/usePageViewTracker";

/**
 * Inline WhatsApp SVG (lucide-react doesn't ship a WhatsApp brand glyph).
 * Mono-color, inherits `currentColor` like the lucide icons.
 */
const WhatsAppIcon = ({ className = "", strokeWidth = 1.5 }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    <path d="M8.5 10.5c.3 1.5 1.4 2.8 2.7 3.6.6.4 1.6.7 2.3.6.4-.1.7-.4 1-.7s.2-.7-.2-.9l-1.1-.5a.7.7 0 0 0-.7.1l-.4.4c-.7-.3-1.4-.9-1.9-1.7l.4-.4a.7.7 0 0 0 .1-.7l-.5-1.1c-.2-.4-.6-.4-.9-.2s-.6.6-.7 1c-.1.2-.1.4-.1.5z" />
  </svg>
);

/** X / Twitter brand glyph */
const XIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SOCIAL_LINKS = [
  { key: "whatsapp", Icon: WhatsAppIcon, color: "#25D366" },
  { key: "linkedin", Icon: Linkedin, color: "#0A66C2" },
  { key: "facebook", Icon: Facebook, color: "#1877F2" },
  { key: "twitter", Icon: XIcon, color: "#F2EBE5" },
];

/**
 * A discreet, editorial share row.
 * - WhatsApp / LinkedIn / Facebook / X open share intents in a new tab
 * - "Copier le lien" copies the URL to clipboard with sonner toast feedback
 * - Mobile gets the native Web Share API when available (single button)
 */
export const ShareBar = ({ title, url }) => {
  const { t } = useLanguage();

  const shareUrl = typeof url === "string" && url.length > 0
    ? url
    : (typeof window !== "undefined" ? window.location.href : "");
  const shareText = title || "Kiwami Studios & Co — L'instant magnifié.";

  const intents = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
  };

  const onCopy = useCallback(async () => {
    // Track copy-link as its own social "network" so the admin sees it
    // alongside whatsapp/linkedin/facebook/x in the analytics breakdown.
    track("share_click", { meta: { network: "copy_link", url: shareUrl } });
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("detail.share_copied"));
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(t("detail.share_copied"));
    }
  }, [shareUrl, t]);

  return (
    <div
      data-testid="share-bar"
      className="flex flex-col items-center gap-5 py-12 border-t border-[#C5A059]/15"
    >
      <p className="overline text-[#C5A059]">{t("detail.share")}</p>
      <div className="flex items-center gap-3 md:gap-4">
        {SOCIAL_LINKS.map(({ key, Icon, color }) => (
          <a
            key={key}
            href={intents[key]}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`share-${key}`}
            title={t(`detail.share_${key}`)}
            aria-label={t(`detail.share_${key}`)}
            // Fire-and-forget analytics ping so the admin dashboard can
            // surface which networks actually drive shares.
            onClick={() =>
              track("share_click", { meta: { network: key, url: shareUrl } })
            }
            className="w-11 h-11 rounded-full border border-[#F2EBE5]/15 text-[#F2EBE5] flex items-center justify-center transition-all duration-300 hover:border-current hover:scale-105"
            style={{ "--hover-color": color }}
            onMouseEnter={(e) => (e.currentTarget.style.color = color)}
            onMouseLeave={(e) => (e.currentTarget.style.color = "")}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
          </a>
        ))}
        <button
          type="button"
          onClick={onCopy}
          data-testid="share-copy"
          title={t("detail.share_copy")}
          aria-label={t("detail.share_copy")}
          className="w-11 h-11 rounded-full border border-[#F2EBE5]/15 text-[#F2EBE5] flex items-center justify-center transition-colors hover:text-[#C5A059] hover:border-[#C5A059]/50"
        >
          <Link2 className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};

export default ShareBar;
